import {
  TextFieldProps,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Select as MuiSelect,
  SelectProps as MuiSelectProps,
  Autocomplete,
  AutocompleteProps,
  AutocompleteRenderInputParams,
} from "@mui/material";
import {
  FastField,
  Field,
  FieldInputProps,
  FieldMetaProps,
  FieldProps,
} from "formik";
import { ChangeEvent, ReactElement, ReactNode, useMemo } from "react";
import {
  FieldValues,
  RefCallBack,
  useController,
  UseControllerProps,
} from "react-hook-form";
import { FormattedMessage } from "react-intl";
import { v4 as uuidv4 } from "uuid";

import { palette } from "~frontend/theme";

import FormTextField from "./FormTextField";

export type BaseSelectProps = Pick<
  TextFieldProps,
  | "children"
  | "className"
  | "disabled"
  | "fullWidth"
  | "label"
  | "name"
  | "onBlur"
  | "error"
  | "placeholder"
  | "InputProps"
  | "sx"
> &
  Pick<
    MuiSelectProps,
    "MenuProps" | "renderValue" | "native" | "displayEmpty"
  > & {
    variant?: "outlined" | "underlined";
  };

export type SelectProps = BaseSelectProps & {
  withNoneOption?: boolean;
  noneOptionLabel?: string;
  value?: string;
  onChange?: React.ChangeEventHandler<{ value: string }>;
};

export type MultiSelectProps = BaseSelectProps & {
  value?: string[];
  onChange?: React.ChangeEventHandler<{ value: string[] }>;
};

export default function Select({
  withNoneOption,
  noneOptionLabel = "None",
  value,
  onChange,
  children,
  MenuProps,
  renderValue,
  className,
  disabled,
  fullWidth,
  label,
  name,
  onBlur,
  error,
  variant = "outlined",
  native,
  displayEmpty,
  ...textFieldProps
}: SelectProps & {
  helperText?: string;
  inputRef?: RefCallBack;
}): ReactElement {
  const id = useMemo(() => uuidv4(), []);

  switch (variant) {
    case "outlined":
      return (
        // @ts-ignore disableUnderline isn't technically allowed if you pass variant="outlined"
        <FormTextField
          select
          value={value}
          onChange={onChange}
          SelectProps={{
            displayEmpty: true,
            MenuProps,
            renderValue,
          }}
          className={className}
          disabled={disabled}
          fullWidth={fullWidth}
          label={label}
          name={name}
          onBlur={onBlur}
          error={error}
          {...textFieldProps}
        >
          {withNoneOption ? (
            <MenuItem key="NONE" value="">
              <span style={{ color: palette.darkGray }}>{noneOptionLabel}</span>
            </MenuItem>
          ) : null}
          {children}
        </FormTextField>
      );
    // TODO: do we need this AND SecondaryDropdown?
    case "underlined":
      return (
        <FormControl
          variant="standard"
          className={className}
          fullWidth={fullWidth}
        >
          {label ? (
            <InputLabel shrink id={id}>
              {label}
            </InputLabel>
          ) : null}
          <MuiSelect
            labelId={id}
            variant="standard"
            value={value}
            onChange={event =>
              onChange && onChange(event as ChangeEvent<{ value: string }>)
            }
            disabled={disabled}
            fullWidth={fullWidth}
            name={name}
            onBlur={onBlur}
            error={error}
            MenuProps={MenuProps}
            renderValue={renderValue}
            sx={textFieldProps?.sx}
            inputRef={textFieldProps.inputRef}
            native={native}
            displayEmpty={displayEmpty}
          >
            {children}
          </MuiSelect>
        </FormControl>
      );
  }
}

export function MultiSelect({
  value,
  onChange,
  MenuProps,
  children,
  renderValue,
  variant = "outlined",
  className,
  disabled,
  fullWidth,
  label,
  name,
  onBlur,
  error,
  ...textFieldProps
}: MultiSelectProps & {
  helperText?: string;
  inputRef?: RefCallBack;
}): ReactElement {
  const id = useMemo(() => uuidv4(), []);

  switch (variant) {
    case "outlined":
      return (
        // @ts-ignore disableUnderline isn't technically allowed if you pass variant="outlined"
        <FormTextField
          select
          name={name}
          label={label}
          value={value}
          // @ts-ignore
          onChange={onChange}
          onBlur={onBlur}
          error={error}
          SelectProps={{
            displayEmpty: true,
            multiple: true,
            MenuProps,
            renderValue,
          }}
          className={className}
          disabled={disabled}
          fullWidth={fullWidth}
          {...textFieldProps}
        >
          {children}
        </FormTextField>
      );
    // TODO: do we need this AND SecondaryMultiSelect?
    case "underlined":
      return (
        <FormControl
          variant="standard"
          className={className}
          fullWidth={fullWidth}
        >
          <InputLabel shrink id={id}>
            {label}
          </InputLabel>
          <MuiSelect
            labelId={id}
            variant="standard"
            value={value}
            // @ts-ignore
            onChange={onChange}
            disabled={disabled}
            fullWidth={fullWidth}
            name={name}
            onBlur={onBlur}
            error={error}
            MenuProps={MenuProps}
            renderValue={renderValue}
            sx={textFieldProps?.sx}
            inputRef={textFieldProps.inputRef}
            multiple
          >
            {children}
          </MuiSelect>
          {error || textFieldProps.helperText ? (
            <FormHelperText error={!!error}>
              {error || textFieldProps.helperText}
            </FormHelperText>
          ) : null}
        </FormControl>
      );
  }
}

export function FormikSelect({
  name,
  fast,
  overrideFormikProps,
  bottomLabel,
  ...selectProps
}: Omit<SelectProps, "value"> & {
  name: string;
  fast?: boolean;
  bottomLabel?: string;
  overrideFormikProps?: (
    field: FieldInputProps<unknown>,
    meta: FieldMetaProps<unknown>
  ) => FieldInputProps<unknown>;
}): ReactElement {
  const Component = fast ? FastField : Field;
  return (
    <Component name={name}>
      {({ field: { ...formikField }, meta }: FieldProps) => {
        const formikProps = overrideFormikProps
          ? overrideFormikProps(formikField, meta)
          : formikField;
        const error = meta.touched ? meta.error : null;

        const SelectComponent = formikProps.multiple ? MultiSelect : Select;

        return (
          <SelectComponent
            {...selectProps}
            {...formikProps}
            error={!!error}
            helperText={error || bottomLabel}
            onChange={(event: ChangeEvent<{ value: any }>) => {
              selectProps.onChange?.(event);
              formikProps.onChange(event);
            }}
          />
        );
      }}
    </Component>
  );
}

export interface SearchableSelectSingleProps<IdType extends string | number> {
  label: string | ReactElement;
  options: { label: string; id: IdType }[];
  value?: IdType;
  onChange?: (newId?: IdType) => void | Promise<void>;
  onInputChange?: (value: string) => void;
  error?: string | null | undefined;
  helperText?: string;
  multiple?: false;
  placeholder?: string;
  renderOption?: AutocompleteProps<
    { label: string; id: IdType },
    false,
    false,
    false
  >["renderOption"];
  disableCloseOnSelect?: boolean;
  sx?: AutocompleteProps<
    { label: string; id: IdType },
    false,
    false,
    false
  >["sx"];
  fullWidth?: boolean;
  disabled?: boolean;
}

export interface SearchableSelectMultipleProps<IdType extends string | number> {
  label: string | ReactElement;
  options: { label: string; id: IdType }[];
  value?: IdType[];
  onChange?: (newIds: IdType[]) => void | Promise<void>;
  onInputChange?: (value: string) => void;
  error?: string | null | undefined;
  helperText?: string;
  multiple: true;
  placeholder?: string;
  renderOption?: AutocompleteProps<
    { label: string; id: IdType },
    true,
    false,
    false
  >["renderOption"];
  disableCloseOnSelect?: boolean;
  sx?: AutocompleteProps<
    { label: string; id: IdType },
    true,
    false,
    false
  >["sx"];
  fullWidth?: boolean;
  disabled?: boolean;
}

export type SearchableSelectProps<IdType extends string | number> =
  | SearchableSelectSingleProps<IdType>
  | SearchableSelectMultipleProps<IdType>;

export function SearchableSelect<IdType extends string | number>(
  props: SearchableSelectProps<IdType>
): React.ReactElement {
  type OptionType = { label: string; id: IdType };

  const {
    label,
    options,
    onInputChange,
    error,
    helperText,
    placeholder,
    disableCloseOnSelect,
    sx,
    fullWidth,
    disabled,
  } = props;

  const commonProps = {
    onInputChange: (_e: React.SyntheticEvent, value: string) =>
      onInputChange?.(value),
    size: "small" as const,
    options,
    renderInput: (params: AutocompleteRenderInputParams) => (
      <FormTextField
        {...params}
        variant="outlined"
        label={label}
        error={!!error}
        helperText={helperText}
        placeholder={placeholder}
      />
    ),
    sx,
    fullWidth,
    disabled,
  };

  if (props.multiple) {
    const selectedOptions = props.value
      ? options.filter(opt => props.value!.includes(opt.id))
      : [];

    return (
      <Autocomplete<OptionType, true, false, false>
        {...commonProps}
        multiple
        value={selectedOptions}
        onChange={async (_e, newValue) => {
          const newIds = newValue.map(v => v.id);
          if (props.onChange) {
            await props.onChange(newIds);
          }
        }}
        disableCloseOnSelect={disableCloseOnSelect ?? true}
        renderOption={
          props.renderOption as AutocompleteProps<
            OptionType,
            true,
            false,
            false
          >["renderOption"]
        }
      />
    );
  }

  return (
    <Autocomplete<OptionType, false, false, false>
      {...commonProps}
      value={
        props.value
          ? options.find(option => option.id === props.value)
          : undefined
      }
      onChange={async (_e, newValue) => {
        if (props.onChange) {
          await props.onChange(newValue?.id);
        }
      }}
      renderOption={
        props.renderOption as AutocompleteProps<
          OptionType,
          false,
          false,
          false
        >["renderOption"]
      }
    />
  );
}

export function FormikSearchableSelect<IdType extends string | number>({
  name,
  fast,
  ...props
}: Omit<
  SearchableSelectSingleProps<IdType>,
  "error" | "onChange" | "onInputChange" | "value"
> & {
  name: string;
  fast?: boolean;
  fullWidth?: boolean;
}): React.ReactElement {
  const Component = fast ? FastField : Field;

  return (
    <Component name={name}>
      {({ field: { ...formikField }, meta }: FieldProps) => {
        return (
          <SearchableSelect
            {...props}
            onChange={val => {
              formikField.onChange({
                target: { name: formikField.name, value: val },
              });
            }}
            error={meta.error}
            helperText={meta.error}
            value={formikField.value}
          />
        );
      }}
    </Component>
  );
}

export function RhfSearchableSelect<
  T extends FieldValues,
  U extends string | number,
>(
  props: UseControllerProps<T> &
    SearchableSelectMultipleProps<U> & {
      bottomLabel?: string;
      label: string;
    }
): React.ReactElement {
  const {
    name,
    control,
    rules,
    defaultValue,
    shouldUnregister,
    ...searchableSelectProps
  } = props;
  const { field, fieldState } = useController({
    name,
    control,
    rules,
    defaultValue,
    shouldUnregister,
  });

  return (
    <SearchableSelect
      {...searchableSelectProps}
      onChange={val => {
        field.onChange({
          target: { name: field.name, value: val },
        });
      }}
      error={fieldState.error?.message}
      helperText={
        fieldState.error?.message || searchableSelectProps.bottomLabel
      }
      value={field.value}
      multiple={searchableSelectProps.multiple}
    />
  );
}

export function MultiSelectValue<ValueType extends string>({
  selected,
  options,
}: {
  selected: ValueType[];
  options: { value: ValueType; label: ReactNode }[];
}): ReactElement {
  if (!selected.length || selected.length === options.length) {
    return (
      <FormattedMessage
        defaultMessage="All"
        description="Multi select value with all selected"
      />
    );
  }

  const selectedLabels = selected.map(
    value => options.find(o => o.value === value)!.label
  );

  if (selectedLabels.length === 1) {
    return <>{selectedLabels[0]}</>;
  }

  if (selected.length === options.length - 1) {
    const notSelected = options.filter(
      option => !selected.includes(option.value)
    )[0];

    return (
      <FormattedMessage
        defaultMessage="Not {excludedValue}"
        description="Multi select value with all but one selected"
        values={{ excludedValue: notSelected.label }}
      />
    );
  }

  return (
    <FormattedMessage
      defaultMessage="{firstValue} and {numOthers} more"
      description="Multi select value with multiple options selected"
      values={{
        firstValue: selectedLabels[0],
        numOthers: selectedLabels.length - 1,
      }}
    />
  );
}
